import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ShieldCheck, UserPlus, Trash2, Save, Users, Info } from "lucide-react";

/**
 * Streamer Staff & Permissions panel (Discord-style).
 * - Owner adds staff by username
 * - Picks role preset (Moderator / Super Moderator)
 * - Fine-tunes individual perms via checkboxes
 * - Lists current staff, edit/remove inline
 */
const ALL_PERMS = [
  { key: "mute",               label: "Mute chatters",                 tier: "moderator" },
  { key: "kick",               label: "Kick from stream",              tier: "moderator" },
  { key: "timeout",            label: "Timeout users",                 tier: "moderator" },
  { key: "ban",                label: "Ban from channel",              tier: "super" },
  { key: "permaban",           label: "Permaban (never expires)",      tier: "super" },
  { key: "invite",             label: "Invite users to party rooms",   tier: "super" },
  { key: "send_notices",       label: "Send notices to followers",     tier: "super" },
  { key: "create_invitations", label: "Create shareable invite links", tier: "super", comingSoon: true },
  { key: "open_games",         label: "Open / reset streamer games",   tier: "super", comingSoon: true },
];

const ROLE_LABEL = { moderator: "Moderator", super_moderator: "Super Moderator" };

const EMPTY_PERMS = Object.fromEntries(ALL_PERMS.map(p => [p.key, false]));
const MOD_PRESET = { ...EMPTY_PERMS, mute: true, kick: true, timeout: true };
const SUPER_PRESET = Object.fromEntries(ALL_PERMS.map(p => [p.key, true]));

const presetFor = (role) => (role === "super_moderator" ? { ...SUPER_PRESET } : { ...MOD_PRESET });

export default function StaffCard({ channelUsername }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  // Add form
  const [newUsername, setNewUsername] = useState("");
  const [newRole, setNewRole] = useState("moderator");
  const [newPerms, setNewPerms] = useState(MOD_PRESET);
  const [busy, setBusy] = useState(false);
  // Inline edit
  const [editingUser, setEditingUser] = useState(null);   // username being edited
  const [editRole, setEditRole] = useState("moderator");
  const [editPerms, setEditPerms] = useState(EMPTY_PERMS);

  const load = () => {
    if (!channelUsername) return;
    setLoading(true);
    api.get(`/channels/${channelUsername}/staff`)
      .then(r => setStaff(r.data || []))
      .catch(e => setErr(e?.response?.data?.detail || "Failed to load staff"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [channelUsername]); // eslint-disable-line react-hooks/exhaustive-deps

  // When new role changes, snap perms to that preset (streamer can then tweak)
  const onNewRoleChange = (role) => {
    setNewRole(role);
    setNewPerms(presetFor(role));
  };
  const onEditRoleChange = (role) => {
    setEditRole(role);
    setEditPerms(presetFor(role));
  };

  const addStaff = async (e) => {
    e.preventDefault();
    if (!newUsername.trim()) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/channels/${channelUsername}/staff`, {
        username: newUsername.trim(),
        role: newRole,
        perms: newPerms,
      });
      toast.success(`Added ${newUsername.trim()} as ${ROLE_LABEL[newRole]}`);
      setNewUsername(""); setNewRole("moderator"); setNewPerms(MOD_PRESET);
      load();
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Failed to add staff";
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const startEdit = (s) => {
    setEditingUser(s.staff_username);
    setEditRole(s.role);
    setEditPerms({ ...EMPTY_PERMS, ...(s.perms || {}) });
  };
  const cancelEdit = () => setEditingUser(null);
  const saveEdit = async () => {
    setBusy(true); setErr("");
    try {
      await api.patch(`/channels/${channelUsername}/staff/${editingUser}`, {
        role: editRole, perms: editPerms,
      });
      toast.success("Permissions updated");
      setEditingUser(null);
      load();
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Update failed";
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const removeStaff = async (username) => {
    if (!window.confirm(`Remove ${username} from your staff team?`)) return;
    try {
      await api.delete(`/channels/${channelUsername}/staff/${username}`);
      toast.success(`Removed ${username}`);
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Remove failed");
    }
  };

  const modPerms = useMemo(() => ALL_PERMS.filter(p => p.tier === "moderator"), []);
  const superPerms = useMemo(() => ALL_PERMS.filter(p => p.tier === "super"), []);

  return (
    <div className="sc-card p-6 space-y-5" data-testid="staff-card">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-[color:var(--sc-cyan)]" />
        <div className="sc-heading text-xl font-bold">Channel Staff & Permissions</div>
      </div>
      <p className="text-xs text-zinc-400 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-zinc-500" />
        <span>Add Moderators and Super Moderators to your channel. Pick a role for the preset,
        then fine-tune individual permissions with the checkboxes.</span>
      </p>

      {/* Add staff form */}
      <form onSubmit={addStaff} className="border border-white/10 rounded-sm p-4 bg-black/20 space-y-4" data-testid="staff-add-form">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className="sc-label mb-1 block">Username</label>
            <input
              data-testid="staff-add-username"
              value={newUsername}
              onChange={e => setNewUsername(e.target.value)}
              placeholder="username to promote"
              className="sc-input text-sm"
              autoCapitalize="off"
              autoCorrect="off"
            />
          </div>
          <div>
            <label className="sc-label mb-1 block">Role</label>
            <select
              data-testid="staff-add-role"
              value={newRole}
              onChange={e => onNewRoleChange(e.target.value)}
              className="sc-input text-sm"
            >
              <option value="moderator">Moderator</option>
              <option value="super_moderator">Super Moderator</option>
            </select>
          </div>
        </div>

        <PermGrid
          modPerms={modPerms}
          superPerms={superPerms}
          perms={newPerms}
          onChange={(key, val) => setNewPerms(p => ({ ...p, [key]: val }))}
          testidPrefix="staff-add-perm"
        />

        {err && <div className="text-xs text-[color:var(--sc-pink)]" data-testid="staff-add-error">{err}</div>}

        <button
          type="submit"
          disabled={busy || !newUsername.trim()}
          className="sc-btn text-sm inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="staff-add-submit"
        >
          <UserPlus className="w-4 h-4" /> {busy ? "Adding…" : "Add Staff Member"}
        </button>
      </form>

      {/* Staff list */}
      <div>
        <div className="sc-label mb-2 flex items-center gap-1.5">
          <Users className="w-3 h-3" /> Your Staff Team ({staff.length})
        </div>
        {loading && <div className="text-xs text-zinc-500 py-2">Loading…</div>}
        {!loading && staff.length === 0 && (
          <div className="text-xs text-zinc-500 py-4 text-center border border-dashed border-white/10 rounded-sm" data-testid="staff-empty">
            No staff yet. Add trusted mods above to help you run your channel.
          </div>
        )}
        <div className="space-y-2">
          {staff.map(s => (
            <div
              key={s.staff_username}
              className="border border-white/10 rounded-sm p-3 bg-black/20"
              data-testid={`staff-row-${s.staff_username}`}
            >
              {editingUser === s.staff_username ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm font-semibold">{s.staff_username}</div>
                    <select
                      value={editRole}
                      onChange={e => onEditRoleChange(e.target.value)}
                      className="sc-input text-xs w-44"
                      data-testid={`staff-edit-role-${s.staff_username}`}
                    >
                      <option value="moderator">Moderator</option>
                      <option value="super_moderator">Super Moderator</option>
                    </select>
                  </div>
                  <PermGrid
                    modPerms={modPerms}
                    superPerms={superPerms}
                    perms={editPerms}
                    onChange={(key, val) => setEditPerms(p => ({ ...p, [key]: val }))}
                    testidPrefix={`staff-edit-perm-${s.staff_username}`}
                  />
                  <div className="flex gap-2 pt-2 border-t border-white/10">
                    <button
                      onClick={saveEdit}
                      disabled={busy}
                      className="sc-btn text-xs inline-flex items-center gap-1.5"
                      data-testid={`staff-save-${s.staff_username}`}
                    >
                      <Save className="w-3 h-3" /> Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="sc-btn-ghost text-xs"
                      data-testid={`staff-cancel-${s.staff_username}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-2">
                      {s.staff_username}
                      <RoleBadge role={s.role} />
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1 truncate">
                      {activePermsSummary(s.perms)}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(s)}
                      className="sc-btn-ghost text-xs"
                      data-testid={`staff-edit-${s.staff_username}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => removeStaff(s.staff_username)}
                      className="sc-btn-ghost text-xs text-[color:var(--sc-pink)] inline-flex items-center gap-1"
                      data-testid={`staff-remove-${s.staff_username}`}
                    >
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PermGrid({ modPerms, superPerms, perms, onChange, testidPrefix }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <div className="sc-label mb-2" style={{ color: "var(--sc-cyan)" }}>Moderator Actions</div>
        <div className="space-y-1.5">
          {modPerms.map(p => (
            <PermCheckbox
              key={p.key}
              perm={p}
              checked={!!perms[p.key]}
              onChange={(v) => onChange(p.key, v)}
              testid={`${testidPrefix}-${p.key}`}
            />
          ))}
        </div>
      </div>
      <div>
        <div className="sc-label mb-2" style={{ color: "var(--sc-orange)" }}>Super Moderator Actions</div>
        <div className="space-y-1.5">
          {superPerms.map(p => (
            <PermCheckbox
              key={p.key}
              perm={p}
              checked={!!perms[p.key]}
              onChange={(v) => onChange(p.key, v)}
              testid={`${testidPrefix}-${p.key}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PermCheckbox({ perm, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer hover:text-white select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="accent-[color:var(--sc-cyan)] w-4 h-4"
        data-testid={testid}
      />
      <span>{perm.label}</span>
      {perm.comingSoon && (
        <span className="text-[9px] uppercase tracking-widest text-zinc-500 border border-white/10 px-1 py-[1px] rounded-sm sc-mono">
          soon
        </span>
      )}
    </label>
  );
}

function RoleBadge({ role }) {
  const isSuper = role === "super_moderator";
  return (
    <span
      className={`text-[9px] uppercase tracking-widest sc-mono px-1.5 py-0.5 rounded-sm border ${
        isSuper
          ? "border-[color:var(--sc-orange)] text-[color:var(--sc-orange)]"
          : "border-[color:var(--sc-cyan)] text-[color:var(--sc-cyan)]"
      }`}
    >
      {isSuper ? "Super Mod" : "Mod"}
    </span>
  );
}

function activePermsSummary(perms) {
  const on = ALL_PERMS.filter(p => (perms || {})[p.key]).map(p => p.label);
  if (on.length === 0) return "No permissions granted.";
  if (on.length <= 3) return on.join(" · ");
  return `${on.slice(0, 3).join(" · ")} +${on.length - 3} more`;
}
